/**
 * DOCUMENTATION:
 * Component: SystemDesignLoadBalancing
 * Purpose: Teaching Load Balancing algorithms and High Availability (Redundancy).
 * * * Note for Junior Developers:
 * - Load Balancer (LB): Sits in front of your servers and routes client requests.
 * - Health Checks: The LB constantly "pings" your servers. If one dies, the LB stops
 * sending traffic to it automatically.
 * - Round Robin: The simplest algorithm—sending requests to servers in order (1, 2, 3, 1...).
 * - Failover: The process of switching to a redundant/backup power source, system,
 * or network upon the failure of the previously active one.
 * * FIX: Implemented react-katex to handle mathematical formulas and resolve
 * ESLint 'no-undef' errors regarding MTBF and MTTR.
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack, List, ListItem, ListItemIcon, ListItemText
} from "@mui/material";

/* ✅ Math Rendering Imports */
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';

/* Icons */
import CallSplitIcon from '@mui/icons-material/CallSplit';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BackupIcon from '@mui/icons-material/Backup';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const AlgorithmChip = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(0, 200, 255, 0.05)",
    border: "1px solid rgba(0, 200, 255, 0.2)",
    borderRadius: "10px",
    height: "100%",
}));

export default function SystemDesignLoadBalancing() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const algorithms = [
        { name: "Round Robin", desc: "Requests are distributed sequentially across the list of servers." },
        { name: "Least Connections", desc: "Sends traffic to the server with the fewest active sessions." },
        { name: "IP Hash", desc: "The client's IP determines which server they hit. Useful for 'Sticky Sessions'." },
    ];

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    LOAD BALANCING & HA
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. What is a Load Balancer? */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><CallSplitIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>The Traffic Cop</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            A Load Balancer acts as a single point of contact for clients. It accepts incoming traffic and
                            spreads it across a "Server Farm." Without this, scaling horizontally would be impossible
                            because users wouldn't know which IP address to connect to.
                        </Typography>



                        <Typography variant="h6" sx={{ mt: 4, mb: 2, color: "#00c8ff" }}>Common Algorithms</Typography>
                        <Grid container spacing={2}>
                            {algorithms.map((algo, i) => (
                                <Grid item xs={12} md={4} key={i}>
                                    <AlgorithmChip>
                                        <Typography variant="subtitle1" fontWeight={700}>{algo.name}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>{algo.desc}</Typography>
                                    </AlgorithmChip>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. High Availability */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#4ade80" }}><HealthAndSafetyIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>High Availability (HA)</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            High Availability means your system is designed to avoid <strong>Single Points of Failure (SPOF)</strong>.
                            If your Load Balancer itself dies, your whole app goes down. To solve this, we use a
                            "Passive" Load Balancer that takes over if the "Active" one fails.
                        </Typography>



                        <Typography variant="h6" sx={{ mt: 4, mb: 2, color: "#4ade80" }}>The HA Math</Typography>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                            We measure availability using "Nines." For example, 99.9% (Three Nines) means your system
                            can only be down for 8.77 hours <em>per year</em>. The formula for Availability ($A$) is:
                        </Typography>

                        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.3)", borderRadius: "8px", textAlign: "center", mb: 3, color: '#4ade80' }}>
                            {/* ✅ FIXED: BlockMath uses a string prop to avoid JSX variable parsing errors for MTBF and MTTR */}
                            <BlockMath math="A = \frac{MTBF}{MTBF + MTTR}" />

                            <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 1 }}>
                                MTBF = Mean Time Between Failures | MTTR = Mean Time To Repair
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <BackupIcon sx={{ color: "#4ade80" }} />
                                    <Box>
                                        <Typography fontWeight={700}>Redundancy</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>Having duplicates of critical components.</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <SettingsInputComponentIcon sx={{ color: "#4ade80" }} />
                                    <Box>
                                        <Typography fontWeight={700}>Failover</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>The automatic switch to a standby system.</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
